/**
 * Phase I-B.2A — Shared Project Add/Edit form (Admin + Builder).
 *
 * Single ownership: this component is the ONLY UI surface that drives
 * Project create/edit. Admin mounts it via the Edit dialog; Builder
 * mounts it inside `BuilderProjectsTab.jsx` for own-projects.
 *
 * Architecture
 * ------------
 *   Section 1 — Project Details
 *   Section 2 — Location (incl. map pin + private address)
 *   Section 3 — Amenities (catalog IDs + legacy preservation)
 *   Section 4 — Available Configurations (canonical, repeatable)
 *   Section 5 — Review (validation summary + final Save)
 *
 * Hydration
 * ---------
 *   • Lazy useState initializer materialises form state SYNCHRONOUSLY
 *     from the project prop at first paint (no blank flicker).
 *   • A `hydratedRef` keyed by (projectId, locationsKey, userTouched)
 *     short-circuits late-arriving location/schema data from clobbering
 *     user edits.
 *   • Schema cache refresh (useDataSync 'config') NEVER resets `form`.
 *
 * Canonical configurations
 * ------------------------
 *   • GET /api/projects/{id}/configurations on mount → adopt either
 *     `source=legacy` (adapted units) or `source=canonical` rows.
 *   • Local edits stay in `form.configurations` array; archive flips
 *     `archived_at` locally and posts the same on Save (backend
 *     enforces archive-vs-delete semantics).
 *   • Save: PATCH /api/builder/projects/{pid} (root fields) +
 *     POST /api/projects/{pid}/configurations (atomic replace-all).
 *   • Cancel: zero network calls; local form state discarded.
 *
 * Legacy preservation
 * -------------------
 *   • Orphan main_area / sub_area → `__legacy__::<raw>` token rendered
 *     in the SelectItems as "Legacy: <raw>".
 *   • Orphan amenities (free-text not in catalog) → still rendered as
 *     individual chips with a "Legacy" badge; persist as-is.
 *   • Legacy unit BHK keys → preserved via the same legacy-token shape.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { apiErrorMsg } from "../../lib/api";
import { useConfig } from "../../lib/ConfigContext";
import { useDataSync, emitDataChange } from "../../lib/dataSync";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { Save, Plus, Trash2, RotateCcw, AlertTriangle, ChevronUp, ChevronDown, FileText, MapPin, Sparkles, Layers, CheckCircle2, X, Lock, Boxes } from "lucide-react";
import { toast } from "sonner";
import MediaUploader from "../MediaUploader";
import GoogleMapsUrlInput from "../../shared/components/GoogleMapsUrlInput";
import {
  fetchProjectSchemaPublic,
  _resetProjectSchemaCache,
} from "../../lib/projectSchemaApi";
import {
  normalizeStatus,
  normalizeMainArea,
  normalizeSubArea,
  legacyTokenFor,
  isLegacyToken,
  legacyTokenRaw,
} from "../../lib/projectAdapters";

// ─── small primitives ──────────────────────────────────────────────────────

const Label = ({ children, required, small, htmlFor }) => (
  <label htmlFor={htmlFor} className={`block ${small ? "text-[10px]" : "text-[11px]"} uppercase tracking-wider font-semibold text-[#52525B] mb-1`}>
    {children}{required && <span className="text-[#ef4444] ml-0.5">*</span>}
  </label>
);

const Field = ({ label, required, value, onChange, type = "text", placeholder, testid }) => (
  <div>
    <Label required={required}>{label}</Label>
    <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder || ""} className="rounded-sm" data-testid={testid} />
  </div>
);

const SectionShell = ({ title, icon: Icon, children, testid }) => (
  <div className="space-y-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-[#0044CC]">
      <Icon size={14} strokeWidth={2.2} />
      <h3 className="text-[13px] font-semibold uppercase tracking-wider">{title}</h3>
    </div>
    <div className="bg-white border border-[#E4E4E7] rounded-sm p-5 space-y-4">
      {children}
    </div>
  </div>
);

const SECTIONS_CREATE = [
  { key: "details",  label: "Details",       Icon: FileText },
  { key: "location", label: "Location",      Icon: MapPin },
  { key: "amenities", label: "Amenities",    Icon: Sparkles },
  { key: "configs",  label: "Configurations", Icon: Layers },
  { key: "review",   label: "Review",         Icon: CheckCircle2 },
];

// Edit mode adds an Inventory tab (units require a saved project + saved
// canonical configurations). The Inventory section uses isolated atomic
// endpoints — it does NOT participate in the unified atomic Save.
const SECTIONS_EDIT = [
  { key: "details",  label: "Details",       Icon: FileText },
  { key: "location", label: "Location",      Icon: MapPin },
  { key: "amenities", label: "Amenities",    Icon: Sparkles },
  { key: "configs",  label: "Configurations", Icon: Layers },
  { key: "inventory", label: "Inventory",    Icon: Boxes },
  { key: "review",   label: "Review",         Icon: CheckCircle2 },
];

// ─── empty templates ──────────────────────────────────────────────────────

const emptyConfig = () => ({
  id: null,                       // null → new row (server assigns UUID)
  config_type_key: "",
  config_type_label: "",
  variant_label: "",
  custom_display_name: "",
  min_area: "",
  max_area: "",
  area_unit: "sqft",
  min_price_input: "",
  max_price_input: "",
  price_unit: "lakh",             // INR / Lakh / Crore — must be EXPLICIT
  legacy_available_count: 0,
  public_notes: "",
  sort_order: 0,
  archived_at: null,
  price_review_required: false,
  raw_price_min: null,
  raw_price_max: null,
  canonical_price_min_inr: null,
  canonical_price_max_inr: null,
  // Phase I-B.2C — per-configuration canonical media (e.g. 2 BHK floor
  // plan separate from 3 BHK floor plan). Each card owns its own arrays.
  images: [],
  videos: [],
  source: "canonical",
});

const emptyForm = () => ({
  name: "",
  main_area: "",
  sub_area: "",
  status: "",
  expected_possession: "",
  description: "",
  rera_number: "",
  rera_verified: false,
  private_address: "",
  amenities: "",                  // comma-separated (legacy compatibility)
  images: [],
  videos: [],
  assigned_to_user_id: "",
  google_maps_link: "",
  latitude: null,
  longitude: null,
  configurations: [],
});

// ─── form-from-project (Edit hydration) ───────────────────────────────────

function formFromProject(p, locations) {
  if (!p) return emptyForm();
  // Reverse-parse legacy "Main · Sub" only when structured fields blank.
  let mainArea = p.main_area || "";
  let subArea = p.sub_area || "";
  if (!mainArea && p.location) {
    const parts = String(p.location).split("·").map((s) => s.trim());
    if (!mainArea) mainArea = parts[0] || "";
    if (!subArea) subArea = parts[1] || "";
  }
  const stEval = normalizeStatus(p.status);
  let statusValue = stEval.canonical;
  if (stEval.status === "legacy") statusValue = legacyTokenFor(stEval.raw);
  if (stEval.status === "empty") statusValue = "";
  const maEval = normalizeMainArea(mainArea, locations);
  const mainValue = maEval.status === "legacy" ? legacyTokenFor(maEval.raw) : maEval.canonical;
  const saEval = normalizeSubArea(subArea, mainArea, locations);
  const subValue = saEval.status === "legacy" ? legacyTokenFor(saEval.raw) : saEval.canonical;
  const imgs = Array.isArray(p.images)
    ? p.images
    : (typeof p.images === "string" ? p.images.split(",").map((s) => s.trim()).filter(Boolean) : []);
  const vids = Array.isArray(p.videos) ? p.videos : [];
  return {
    name: p.name || "",
    main_area: mainValue,
    sub_area: subValue,
    status: statusValue,
    expected_possession: p.expected_possession || "",
    description: p.description || "",
    rera_number: p.rera_number || "",
    rera_verified: !!p.rera_verified,
    private_address: p.private_address || "",
    amenities: Array.isArray(p.amenities) ? p.amenities.join(", ") : (p.amenities || ""),
    images: imgs,
    videos: vids,
    assigned_to_user_id: "",
    google_maps_link: p.google_maps_link || "",
    latitude: typeof p.latitude === "number" ? p.latitude : null,
    longitude: typeof p.longitude === "number" ? p.longitude : null,
    configurations: [],            // hydrated separately via /configurations API
  };
}

// ─── price helpers ────────────────────────────────────────────────────────

const PRICE_UNIT_MAP = { inr: 1, lakh: 100000, crore: 10000000 };

function toCanonicalINR(value, unit) {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const mult = PRICE_UNIT_MAP[(unit || "").toLowerCase()] || 0;
  if (!mult) return null;
  return Math.round(n * mult);
}

function formatINRDisplay(inr) {
  if (inr === null || inr === undefined) return "—";
  if (inr >= 10000000) return `₹${(inr / 10000000).toFixed(inr % 10000000 === 0 ? 0 : 2)} Cr`;
  if (inr >= 100000)  return `₹${(inr / 100000).toFixed(inr % 100000 === 0 ? 0 : 2)} L`;
  return `₹${inr.toLocaleString("en-IN")}`;
}

// ─── main component ───────────────────────────────────────────────────────

export default function ProjectForm({
  mode,                           // "create" | "edit"
  project = null,
  currentUser,
  onSaved,
  onCancel,
  embedded = false,               // when true, no outer card wrapper
}) {
  const isAdmin = currentUser?.role === "admin";
  const { locations = [] } = useConfig();

  // ── synchronous hydration ──
  const [form, setForm] = useState(() => (
    mode === "edit" && project ? formFromProject(project, locations) : emptyForm()
  ));
  const [origProject] = useState(project);          // immutable snapshot for diff
  const hydratedRef = useRef(
    mode === "edit" && project
      ? { projectId: project.id, locationsKey: locations.length, userTouched: false }
      : { projectId: null, locationsKey: locations.length, userTouched: false }
  );
  const [activeSection, setActiveSection] = useState("details");
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const submitOnceRef = useRef(false);              // prevent double submit

  // ── schema ──
  const [schema, setSchema] = useState(null);
  const [schemaError, setSchemaError] = useState(null);
  const loadSchema = async () => {
    setSchemaError(null);
    try {
      _resetProjectSchemaCache();
      const s = await fetchProjectSchemaPublic({ force: true });
      setSchema(s);
    } catch (e) {
      setSchemaError(apiErrorMsg(e, "Could not load project schema"));
    }
  };
  useEffect(() => { loadSchema(); }, []);
  useDataSync(["config"], loadSchema);              // schema refresh does NOT touch form

  // ── re-hydration on (projectId, locations) change, gated by userTouched ──
  useEffect(() => {
    if (mode !== "edit" || !project) return;
    const targetId = project.id;
    const locKey = (locations || []).length;
    const ref = hydratedRef.current;
    if (ref.projectId === targetId && ref.locationsKey === locKey) return;
    if (ref.projectId === targetId && ref.userTouched) {
      hydratedRef.current = { ...ref, locationsKey: locKey };
      return;
    }
    setForm((prev) => ({ ...formFromProject(project, locations || []), configurations: prev.configurations || [] }));
    hydratedRef.current = { projectId: targetId, locationsKey: locKey, userTouched: false };
  }, [project, locations, mode]);

  // ── configuration hydration (separate from schema) ──
  const [configsLoaded, setConfigsLoaded] = useState(mode === "create");
  useEffect(() => {
    if (mode !== "edit" || !project?.id) {
      setConfigsLoaded(true);
      return;
    }
    let cancelled = false;
    api.get(`/projects/${project.id}/configurations`).then((r) => {
      if (cancelled) return;
      const rows = (r.data?.configurations || []).map((c) => ({
        ...emptyConfig(),
        ...c,
        // Re-fill INR-derived price_input from canonical for legacy rows
        // that arrived with raw values but no input.
        min_price_input: c.min_price_input ?? c.raw_price_min ?? "",
        max_price_input: c.max_price_input ?? c.raw_price_max ?? "",
        price_unit: c.price_unit || "",
      }));
      setForm((prev) => ({ ...prev, configurations: rows }));
      setConfigsLoaded(true);
    }).catch((e) => {
      if (cancelled) return;
      toast.error(apiErrorMsg(e, "Could not load configurations"));
      setConfigsLoaded(true);
    });
    return () => { cancelled = true; };
  }, [project?.id, mode]);

  // ── field updates ──
  const markTouched = () => {
    if (!hydratedRef.current.userTouched) {
      hydratedRef.current = { ...hydratedRef.current, userTouched: true };
    }
  };
  const update = (k, v) => {
    markTouched();
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };
  const updateConfig = (idx, patch) => {
    markTouched();
    setForm((f) => {
      const next = f.configurations.slice();
      next[idx] = { ...next[idx], ...patch };
      return { ...f, configurations: next };
    });
  };
  const addConfig = () => {
    markTouched();
    setForm((f) => ({
      ...f,
      configurations: [...f.configurations, { ...emptyConfig(), sort_order: (f.configurations.length || 0) * 10 }],
    }));
  };
  const archiveConfig = (idx) => {
    markTouched();
    setForm((f) => {
      const next = f.configurations.slice();
      next[idx] = { ...next[idx], archived_at: new Date().toISOString() };
      return { ...f, configurations: next };
    });
  };
  const restoreConfig = (idx) => {
    markTouched();
    setForm((f) => {
      const next = f.configurations.slice();
      next[idx] = { ...next[idx], archived_at: null };
      return { ...f, configurations: next };
    });
  };
  const moveConfig = (idx, dir) => {
    markTouched();
    setForm((f) => {
      const next = f.configurations.slice();
      const swap = idx + dir;
      if (swap < 0 || swap >= next.length) return f;
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return { ...f, configurations: next.map((c, i) => ({ ...c, sort_order: i * 10 })) };
    });
  };

  // ── schema-derived options ──
  const statusOptions = useMemo(() => {
    if (!schema) return [];
    return (schema.catalog_options || [])
      .filter((o) => o.scope === "project_status")
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      .map((o) => ({ key: o.key, label: o.label }));
  }, [schema]);
  const bhkOptions = useMemo(() => {
    if (!schema) return [];
    return (schema.catalog_options || [])
      .filter((o) => o.scope === "configuration_type")
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      .map((o) => ({ key: o.key, label: o.label }));
  }, [schema]);
  const amenityOptions = useMemo(() => {
    if (!schema) return [];
    return (schema.catalog_options || [])
      .filter((o) => o.scope === "amenity")
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      .map((o) => ({ key: o.key, label: o.label }));
  }, [schema]);

  // ── locations cascade ──
  const mainAreas = locations.map((l) => l.main).filter(Boolean);
  const subAreasFor = (mainName) => {
    const m = locations.find((l) => l.main === mainName);
    return (m?.subs || []).filter(Boolean);
  };

  // ── validation ──
  const validate = () => {
    const errs = {};
    if (!form.name?.trim()) errs.name = "Project name is required";
    if (!form.main_area) errs.main_area = "Main area is required";
    if (!form.status) errs.status = "Project status is required";
    // configurations are optional — but if any present, each must be valid
    (form.configurations || []).forEach((c, i) => {
      if (c.archived_at) return;
      if (!c.config_type_key) errs[`config_${i}_type`] = "Type required";
      if (c.min_area && c.max_area && Number(c.min_area) > Number(c.max_area)) {
        errs[`config_${i}_area`] = "Min area cannot exceed max area";
      }
      if (c.min_price_input !== "" && c.max_price_input !== "" &&
          Number(c.min_price_input) > Number(c.max_price_input)) {
        errs[`config_${i}_price`] = "Min price cannot exceed max price";
      }
      if ((c.min_price_input !== "" || c.max_price_input !== "") && !c.price_unit) {
        errs[`config_${i}_unit`] = "Pick a price unit (Lakh / Crore / INR)";
      }
    });
    setErrors(errs);
    return errs;
  };

  // ── save ──
  // Phase I-B.2A FINAL — One orchestrated server endpoint. We post
  // root fields + canonical configurations in a SINGLE request. The
  // server runs the entire operation atomically (snapshot-and-compensate
  // rollback on any failure). The client never sequences PATCH + POST
  // anymore, so a partial-save state is structurally impossible.
  //
  // Concurrency: we send `expected_version` matching the value we
  // read on open; on a 409 we surface a Reload state.
  //
  // Idempotency: a fresh uuid `idempotency_key` is minted on each Save
  // click. A failed/network-retried POST with the same key returns
  // the cached server response — no duplicate mutate, no duplicate
  // project records.
  const idempotencyRef = useRef(null);
  const [conflict, setConflict] = useState(null);  // {current_version, ...}
  const submit = async () => {
    if (submitOnceRef.current) return;
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setActiveSection(errs.name || errs.main_area || errs.status ? "details" : "review");
      toast.error("Please fix the highlighted fields");
      return;
    }
    submitOnceRef.current = true;
    setBusy(true);
    setConflict(null);
    if (!idempotencyRef.current) {
      // Generate a fresh idempotency key per Save click.
      idempotencyRef.current = (typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
    }
    try {
      const mainSend = isLegacyToken(form.main_area) ? legacyTokenRaw(form.main_area) : form.main_area;
      const subSend  = isLegacyToken(form.sub_area)  ? legacyTokenRaw(form.sub_area)  : form.sub_area;
      const statusSend = isLegacyToken(form.status) ? legacyTokenRaw(form.status) : form.status;
      const amenitiesArr = String(form.amenities || "").split(",").map((s) => s.trim()).filter(Boolean);
      const liveConfigs = (form.configurations || []).filter((c) => !c.archived_at).map((c, i) => ({
        id: c.id && !String(c.id).startsWith("legacy_") ? c.id : undefined,
        config_type_key: c.config_type_key,
        config_type_label: c.config_type_label || (bhkOptions.find((o) => o.key === c.config_type_key)?.label || ""),
        variant_label: c.variant_label || "",
        custom_display_name: c.custom_display_name || "",
        min_area: Number(c.min_area) || 0,
        max_area: Number(c.max_area) || Number(c.min_area) || 0,
        area_unit: c.area_unit || "sqft",
        min_price_input: c.min_price_input === "" ? null : Number(c.min_price_input),
        max_price_input: c.max_price_input === "" ? null : Number(c.max_price_input),
        price_unit: c.price_unit || "",
        legacy_available_count: Number(c.legacy_available_count) || 0,
        public_notes: c.public_notes || "",
        sort_order: i * 10,
        // Phase I-B.2C — per-configuration canonical media. Each row keeps
        // its own array; saving a config only replaces THAT row's media.
        images: Array.isArray(c.images) ? c.images : [],
        videos: Array.isArray(c.videos) ? c.videos : [],
      }));
      const body = {
        project_id: mode === "edit" ? project?.id : null,
        expected_version: mode === "edit" ? Number(project?.configurations_version || 0) : null,
        idempotency_key: idempotencyRef.current,
        root: {
          name: form.name,
          main_area: mainSend,
          sub_area: subSend,
          location: [mainSend, subSend].filter(Boolean).join(" · "),
          status: statusSend,
          expected_possession: form.expected_possession,
          description: form.description,
          rera_number: form.rera_number,
          rera_verified: !!form.rera_verified,
          private_address: form.private_address,
          amenities: amenitiesArr,
          images: form.images,
          videos: form.videos,
          assigned_to_user_id: form.assigned_to_user_id || null,
          google_maps_link: form.google_maps_link || "",
          latitude: form.latitude,
          longitude: form.longitude,
        },
        configurations: liveConfigs,
      };
      const r = await api.post("/projects/editor/save", body);
      // Success — reset idempotency key for the next save click.
      idempotencyRef.current = null;
      toast.success(mode === "create" ? "Project created" : "Project updated");
      emitDataChange("projects", { action: "save", project_id: r.data.project_id });
      onSaved && onSaved(r.data.root || { id: r.data.project_id });
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        // Conflict — surface a reload state, do NOT close the dialog.
        // We keep the idempotency key around — a Retry with the same
        // expected_version that's still stale would also 409 (correct).
        setConflict({
          message: e?.response?.data?.detail || e?.response?.data?.message || "This project changed since you opened it. Reload to see the latest version.",
          raw: e?.response?.data,
        });
      } else {
        // Network or 5xx — keep the idempotency key so the user can
        // retry safely without creating a duplicate project.
        toast.error(apiErrorMsg(e, "Save failed — your changes are still here. Try again."));
      }
    } finally {
      setBusy(false);
      submitOnceRef.current = false;
    }
  };

  // ── schema fail-fast ──
  if (schemaError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-sm p-4 text-[12px] text-red-800 flex items-start gap-2" data-testid="project-form-schema-error">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="font-semibold">Could not load project schema</div>
          <div className="text-red-700 mt-0.5">{schemaError}</div>
        </div>
        <Button onClick={loadSchema} variant="outline" size="sm" className="rounded-sm border-red-300 text-red-700" data-testid="project-form-schema-retry">
          <RotateCcw size={12} className="mr-1.5" /> Retry
        </Button>
      </div>
    );
  }

  if (!schema || !configsLoaded) {
    return <div className="text-[12px] text-[#52525B] p-4">Loading form…</div>;
  }

  // SECTIONS — Inventory tab only renders for saved (edit-mode) projects.
  const SECTIONS = mode === "edit" && project?.id ? SECTIONS_EDIT : SECTIONS_CREATE;

  const sectionErrors = {
    details: errors.name || errors.main_area || errors.status,
    location: errors.main_area,
    amenities: null,
    configs: Object.keys(errors).some((k) => k.startsWith("config_")),
    inventory: null,
    review: Object.keys(errors).length > 0,
  };

  return (
    <div className="space-y-5" data-testid="project-form">
      {/* HTTP 409 conflict banner — Phase I-B.2A FINAL */}
      {conflict && (
        <div className="bg-amber-50 border border-amber-300 rounded-sm p-3 flex items-start gap-2" data-testid="project-form-conflict-banner">
          <AlertTriangle size={14} className="mt-0.5 text-amber-700 shrink-0" />
          <div className="flex-1 text-[12px] text-amber-900">
            <div className="font-semibold mb-0.5">Conflict — this project changed since you opened it.</div>
            <div className="text-amber-800">{conflict.message}</div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-sm border-amber-400 text-amber-800"
            onClick={() => {
              // Reload by closing — parent will reopen with fresh data.
              setConflict(null);
              onCancel && onCancel();
            }}
            data-testid="project-form-conflict-reload"
          >
            <RotateCcw size={12} className="mr-1.5" /> Reload
          </Button>
        </div>
      )}

      {/* Section nav */}
      <div className="flex items-center gap-1 border-b border-[#E4E4E7] overflow-x-auto no-scrollbar">
        {SECTIONS.map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setActiveSection(key)}
            data-testid={`project-form-section-${key}`}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-semibold border-b-2 transition-colors shrink-0
              ${activeSection === key
                ? "border-[#0044CC] text-[#0044CC]"
                : "border-transparent text-[#52525B] hover:text-[#09090B]"}`}
          >
            <Icon size={12} />
            {label}
            {sectionErrors[key] && <AlertTriangle size={11} className="text-red-500" />}
          </button>
        ))}
      </div>

      {/* Section bodies */}
      {activeSection === "details" && (
        <SectionShell title="Project Details" icon={FileText} testid="project-form-details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Project Name" required value={form.name} onChange={(v) => update("name", v)} testid="project-name" />
            <div>
              <Label required>Project Status</Label>
              <Select value={form.status} onValueChange={(v) => update("status", v)}>
                <SelectTrigger className="rounded-sm" data-testid="project-status-trigger"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {isLegacyToken(form.status) && (
                    <SelectItem value={form.status} data-testid="project-status-legacy">
                      Legacy: {legacyTokenRaw(form.status)}
                    </SelectItem>
                  )}
                  {statusOptions.map((o) => (
                    <SelectItem key={o.key} value={o.key} data-testid={`project-status-opt-${o.key}`}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.status && <div className="text-[10px] text-red-600 mt-1">{errors.status}</div>}
            </div>
            <Field label="Expected Possession (YYYY-MM)" value={form.expected_possession} onChange={(v) => update("expected_possession", v)} testid="project-possession" />
            <Field label="RERA Number" value={form.rera_number} onChange={(v) => update("rera_number", v)} testid="project-rera-number" />
            <div className="flex items-center gap-2">
              <Switch checked={!!form.rera_verified} onCheckedChange={(v) => update("rera_verified", v)} data-testid="project-rera-verified" />
              <span className="text-[12px]">RERA verified</span>
            </div>
            {isAdmin && (
              <Field label="Assigned Builder/Dealer (user ID)" value={form.assigned_to_user_id} onChange={(v) => update("assigned_to_user_id", v)} testid="project-assignee-id" placeholder="Optional — assign to a builder/dealer" />
            )}
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={4} value={form.description} onChange={(e) => update("description", e.target.value)} className="rounded-sm" data-testid="project-description" />
          </div>
          {/* Media editing — Phase I-B.2A keeps the existing safe path */}
          <div>
            <Label>Media (photos & videos)</Label>
            <div className="text-[10px] text-[#71717A] mb-2 flex items-center gap-1">
              <Lock size={9} /> Existing media references are preserved byte-identical. New media will be added via the same path as before.
            </div>
            <MediaUploader
              images={form.images}
              videos={form.videos}
              onImagesChange={(next) => { markTouched(); setForm((f) => ({ ...f, images: next })); }}
              onVideosChange={(next) => { markTouched(); setForm((f) => ({ ...f, videos: next })); }}
              testidPrefix="project-media"
            />
          </div>
        </SectionShell>
      )}

      {activeSection === "location" && (
        <SectionShell title="Location" icon={MapPin} testid="project-form-location">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label required>Main Area</Label>
              <Select value={form.main_area} onValueChange={(v) => { update("main_area", v); update("sub_area", ""); }}>
                <SelectTrigger className="rounded-sm" data-testid="project-main-area-trigger"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {isLegacyToken(form.main_area) && (
                    <SelectItem value={form.main_area}>Legacy: {legacyTokenRaw(form.main_area)}</SelectItem>
                  )}
                  {mainAreas.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
              {errors.main_area && <div className="text-[10px] text-red-600 mt-1">{errors.main_area}</div>}
            </div>
            <div>
              <Label>Sub Area</Label>
              <Select value={form.sub_area} onValueChange={(v) => update("sub_area", v)}>
                <SelectTrigger className="rounded-sm" data-testid="project-sub-area-trigger"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {isLegacyToken(form.sub_area) && (
                    <SelectItem value={form.sub_area}>Legacy: {legacyTokenRaw(form.sub_area)}</SelectItem>
                  )}
                  {subAreasFor(form.main_area).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Private Full Address (internal)</Label>
            <Textarea rows={2} value={form.private_address} onChange={(e) => update("private_address", e.target.value)} className="rounded-sm" data-testid="project-private-address" />
          </div>
          <GoogleMapsUrlInput
            value={form.google_maps_link}
            onChange={(v) => update("google_maps_link", v)}
            testIdPrefix="project-maps-url"
          />
          {(form.latitude && form.longitude) && (
            <div className="text-[10px] text-[#15803D]" data-testid="project-coords-display">📍 {form.latitude.toFixed(4)}, {form.longitude.toFixed(4)}</div>
          )}
        </SectionShell>
      )}

      {activeSection === "amenities" && (
        <SectionShell title="Amenities" icon={Sparkles} testid="project-form-amenities">
          <AmenitiesEditor
            value={form.amenities}
            options={amenityOptions}
            onChange={(v) => update("amenities", v)}
          />
        </SectionShell>
      )}

      {activeSection === "configs" && (
        <SectionShell title="Available Configurations" icon={Layers} testid="project-form-configs">
          <ConfigurationsEditor
            configurations={form.configurations}
            bhkOptions={bhkOptions}
            onUpdate={updateConfig}
            onAdd={addConfig}
            onArchive={archiveConfig}
            onRestore={restoreConfig}
            onMove={moveConfig}
            errors={errors}
          />
        </SectionShell>
      )}

      {activeSection === "inventory" && mode === "edit" && project?.id && (
        <SectionShell title="Exact Unit Inventory (internal)" icon={Boxes} testid="project-form-inventory">
          <InventoryEditor projectId={project.id} bhkOptions={bhkOptions} />
        </SectionShell>
      )}

      {activeSection === "review" && (
        <SectionShell title="Review & Save" icon={CheckCircle2} testid="project-form-review">
          <ReviewSummary form={form} statusOptions={statusOptions} bhkOptions={bhkOptions} errors={errors} />
        </SectionShell>
      )}

      {/* Footer actions */}
      <div className="flex items-center justify-between border-t border-[#E4E4E7] pt-4">
        <Button type="button" variant="ghost" onClick={onCancel} data-testid="project-form-cancel" className="text-[11px]">
          <X size={12} className="mr-1.5" /> Cancel
        </Button>
        <div className="flex items-center gap-2">
          {activeSection !== "review" && (
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => setActiveSection(SECTIONS[Math.min(SECTIONS.findIndex((s) => s.key === activeSection) + 1, SECTIONS.length - 1)].key)} data-testid="project-form-next">
              Next: {SECTIONS[Math.min(SECTIONS.findIndex((s) => s.key === activeSection) + 1, SECTIONS.length - 1)].label}
            </Button>
          )}
          <Button type="button" onClick={submit} disabled={busy} className="btn-brand rounded-sm" data-testid="project-form-save">
            <Save size={12} className="mr-1.5" /> {busy ? "Saving…" : (mode === "create" ? "Create Project" : "Save Changes")}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── sub-components ───────────────────────────────────────────────────────

function AmenitiesEditor({ value, options, onChange }) {
  // value is comma-separated string. We split, render chips, and let the user
  // toggle catalog items + add legacy free-text values that still show.
  const tokens = String(value || "").split(",").map((s) => s.trim()).filter(Boolean);
  const known = new Set(options.map((o) => o.label));
  const toggle = (label) => {
    const exists = tokens.includes(label);
    const next = exists ? tokens.filter((t) => t !== label) : [...tokens, label];
    onChange(next.join(", "));
  };
  const [pending, setPending] = useState("");
  const addCustom = () => {
    const v = pending.trim();
    if (!v) return;
    if (!tokens.includes(v)) onChange([...tokens, v].join(", "));
    setPending("");
  };
  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
        {options.map((o) => {
          const active = tokens.includes(o.label);
          return (
            <button
              key={o.key}
              type="button"
              onClick={() => toggle(o.label)}
              data-testid={`amenity-toggle-${o.key}`}
              className={`text-[12px] py-1.5 px-2 rounded-sm border text-left transition-colors
                ${active ? "bg-[#EEF4FF] border-[#C7D9FF] text-[#0044CC] font-semibold" : "bg-white border-[#E4E4E7] text-[#52525B] hover:border-[#0044CC]"}`}
            >
              {active ? "✓ " : "+ "}{o.label}
            </button>
          );
        })}
      </div>
      {/* Legacy chips: tokens not in known catalog */}
      {tokens.filter((t) => !known.has(t)).length > 0 && (
        <div>
          <Label small>Legacy / Custom Amenities</Label>
          <div className="flex flex-wrap gap-1.5" data-testid="amenities-legacy-row">
            {tokens.filter((t) => !known.has(t)).map((t) => (
              <Badge key={t} variant="outline" className="rounded-sm text-[10px] gap-1">
                Legacy: {t}
                <button type="button" onClick={() => onChange(tokens.filter((x) => x !== t).join(", "))} className="ml-1 hover:text-red-600">
                  <X size={9} />
                </button>
              </Badge>
            ))}
          </div>
        </div>
      )}
      {/* Add free-text amenity */}
      <div className="mt-3 flex items-center gap-2">
        <Input value={pending} onChange={(e) => setPending(e.target.value)} placeholder="Add a custom amenity (e.g. Yoga Deck)…" className="rounded-sm" data-testid="amenity-custom-input" />
        <Button type="button" onClick={addCustom} variant="outline" className="rounded-sm" data-testid="amenity-custom-add">
          <Plus size={12} className="mr-1" /> Add
        </Button>
      </div>
    </div>
  );
}

function ConfigurationsEditor({ configurations, bhkOptions, onUpdate, onAdd, onArchive, onRestore, onMove, errors }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] text-[#52525B]">Repeatable cards. Variants of the same BHK (Type A / Type B) require distinct variant labels.</div>
        <Button type="button" onClick={onAdd} variant="outline" className="rounded-sm" data-testid="config-add">
          <Plus size={12} className="mr-1.5" /> Add Configuration
        </Button>
      </div>
      {configurations.length === 0 && (
        <div className="border border-dashed border-[#D4D4D8] rounded-sm p-6 text-center text-[12px] text-[#71717A]">
          No configurations yet. Click <span className="font-semibold">Add Configuration</span> to add one.
        </div>
      )}
      {configurations.map((c, i) => {
        const isCustom = (bhkOptions.find((o) => o.key === c.config_type_key)?.label || "").toLowerCase() === "custom";
        return (
          <div
            key={c.id || `new-${i}`}
            className={`border rounded-sm p-4 ${c.archived_at ? "bg-[#F4F4F5] border-[#E4E4E7] opacity-70" : "bg-white border-[#E4E4E7]"}`}
            data-testid={`config-card-${i}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] font-semibold text-[#52525B] uppercase tracking-wider flex items-center gap-2">
                Configuration #{i + 1}
                {c.archived_at && <Badge variant="outline" className="rounded-sm text-[10px]">Archived</Badge>}
                {c.source === "legacy_units" && <Badge variant="outline" className="rounded-sm text-[10px]">From Legacy</Badge>}
                {c.price_review_required && (
                  <Badge variant="outline" className="rounded-sm text-[10px] bg-amber-50 border-amber-300 text-amber-800">
                    Price review required
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button type="button" size="sm" variant="ghost" onClick={() => onMove(i, -1)} disabled={i === 0} className="h-7 w-7 p-0" data-testid={`config-${i}-up`}>
                  <ChevronUp size={12} />
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => onMove(i, 1)} disabled={i === configurations.length - 1} className="h-7 w-7 p-0" data-testid={`config-${i}-down`}>
                  <ChevronDown size={12} />
                </Button>
                {c.archived_at ? (
                  <Button type="button" size="sm" variant="outline" onClick={() => onRestore(i)} className="rounded-sm h-7 text-[10px]" data-testid={`config-${i}-restore`}>
                    <RotateCcw size={11} className="mr-1" /> Restore
                  </Button>
                ) : (
                  <Button type="button" size="sm" variant="outline" onClick={() => onArchive(i)} className="rounded-sm h-7 text-[10px] text-red-600 border-red-200" data-testid={`config-${i}-archive`}>
                    <Trash2 size={11} className="mr-1" /> Archive
                  </Button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <Label small required>Type</Label>
                <Select value={c.config_type_key} onValueChange={(v) => onUpdate(i, { config_type_key: v })}>
                  <SelectTrigger className="rounded-sm h-8 text-[12px]" data-testid={`config-${i}-type`}>
                    <SelectValue placeholder="Pick" />
                  </SelectTrigger>
                  <SelectContent>
                    {bhkOptions.map((o) => (
                      <SelectItem key={o.key} value={o.key} data-testid={`config-${i}-type-opt-${o.key}`}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors[`config_${i}_type`] && <div className="text-[10px] text-red-600 mt-1">{errors[`config_${i}_type`]}</div>}
              </div>
              <Field label="Variant Label" value={c.variant_label} onChange={(v) => onUpdate(i, { variant_label: v })} placeholder="e.g. Type A" testid={`config-${i}-variant`} />
              {isCustom && <Field label="Custom Display Name" value={c.custom_display_name} onChange={(v) => onUpdate(i, { custom_display_name: v })} testid={`config-${i}-custom-name`} />}
              <Field label="Available Count (legacy)" type="number" value={c.legacy_available_count} onChange={(v) => onUpdate(i, { legacy_available_count: v })} testid={`config-${i}-count`} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
              <Field label="Min Area" type="number" value={c.min_area} onChange={(v) => onUpdate(i, { min_area: v })} testid={`config-${i}-min-area`} />
              <Field label="Max Area" type="number" value={c.max_area} onChange={(v) => onUpdate(i, { max_area: v })} testid={`config-${i}-max-area`} />
              <div>
                <Label small>Area Unit</Label>
                <Select value={c.area_unit} onValueChange={(v) => onUpdate(i, { area_unit: v })}>
                  <SelectTrigger className="rounded-sm h-8 text-[12px]" data-testid={`config-${i}-area-unit`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sqft">sqft</SelectItem>
                    <SelectItem value="sqyd">sqyd</SelectItem>
                    <SelectItem value="sqm">sqm</SelectItem>
                    <SelectItem value="acre">acre</SelectItem>
                  </SelectContent>
                </Select>
                {errors[`config_${i}_area`] && <div className="text-[10px] text-red-600 mt-1">{errors[`config_${i}_area`]}</div>}
              </div>
              <div className="flex items-end">
                <div className="w-full">
                  <Label small>Sort Order</Label>
                  <Input type="number" value={c.sort_order} onChange={(e) => onUpdate(i, { sort_order: Number(e.target.value) })} className="rounded-sm h-8" data-testid={`config-${i}-sort`} />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
              <Field label="Min Price" type="number" value={c.min_price_input} onChange={(v) => onUpdate(i, { min_price_input: v })} testid={`config-${i}-min-price`} />
              <Field label="Max Price" type="number" value={c.max_price_input} onChange={(v) => onUpdate(i, { max_price_input: v })} testid={`config-${i}-max-price`} />
              <div>
                <Label small required>Price Unit</Label>
                <Select value={c.price_unit} onValueChange={(v) => onUpdate(i, { price_unit: v })}>
                  <SelectTrigger className="rounded-sm h-8 text-[12px]" data-testid={`config-${i}-price-unit`}><SelectValue placeholder="Pick" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lakh" data-testid={`config-${i}-price-unit-lakh`}>Lakh (₹L)</SelectItem>
                    <SelectItem value="crore" data-testid={`config-${i}-price-unit-crore`}>Crore (₹Cr)</SelectItem>
                    <SelectItem value="inr" data-testid={`config-${i}-price-unit-inr`}>INR (₹)</SelectItem>
                  </SelectContent>
                </Select>
                {errors[`config_${i}_unit`] && <div className="text-[10px] text-red-600 mt-1">{errors[`config_${i}_unit`]}</div>}
                {errors[`config_${i}_price`] && <div className="text-[10px] text-red-600 mt-1">{errors[`config_${i}_price`]}</div>}
              </div>
              <div>
                <Label small>Computed (INR)</Label>
                <div className="text-[11px] text-[#52525B] py-1.5 px-2 bg-[#F4F4F5] rounded-sm border border-[#E4E4E7]" data-testid={`config-${i}-canonical`}>
                  {formatINRDisplay(toCanonicalINR(c.min_price_input, c.price_unit))}
                  {c.max_price_input !== "" && c.max_price_input !== null && Number(c.max_price_input) !== Number(c.min_price_input) && (
                    <> – {formatINRDisplay(toCanonicalINR(c.max_price_input, c.price_unit))}</>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-3">
              <Label small>Public Notes</Label>
              <Input value={c.public_notes} onChange={(e) => onUpdate(i, { public_notes: e.target.value })} className="rounded-sm h-8 text-[12px]" data-testid={`config-${i}-notes`} />
            </div>
            {/* Phase I-B.2C — Per-configuration canonical media. Each
                card owns its own images/videos array; saving this row
                only replaces THIS row's media (2 BHK media stays
                isolated from 3 BHK media). */}
            <div className="mt-4 pt-4 border-t border-dashed border-[#E4E4E7]">
              <Label small>Configuration Media (e.g. floor plan for this BHK)</Label>
              <div className="text-[10px] text-[#71717A] mb-2">
                Photos / floor plans / video walkthroughs specific to this configuration. Isolated per row.
              </div>
              <MediaUploader
                images={Array.isArray(c.images) ? c.images : []}
                videos={Array.isArray(c.videos) ? c.videos : []}
                onImagesChange={(next) => onUpdate(i, { images: next })}
                onVideosChange={(next) => onUpdate(i, { videos: next })}
                testidPrefix={`config-${i}-media`}
              />
            </div>
            {c.price_review_required && !c.price_unit && (
              <div className="mt-3 bg-amber-50 border border-amber-300 rounded-sm p-2 text-[11px] text-amber-800 flex items-start gap-2" data-testid={`config-${i}-price-warn`}>
                <AlertTriangle size={11} className="mt-0.5" />
                <div>
                  <div className="font-semibold">Ambiguous legacy price detected.</div>
                  This row was imported from legacy data with a numeric price ({c.raw_price_min ?? "—"})
                  but NO unit. Pick Lakh / Crore / INR above to confirm the canonical value before Save.
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ReviewSummary({ form, statusOptions, bhkOptions, errors }) {
  const liveConfigs = (form.configurations || []).filter((c) => !c.archived_at);
  const errorKeys = Object.keys(errors).filter((k) => errors[k]);
  return (
    <div className="space-y-4">
      {errorKeys.length > 0 ? (
        <div className="bg-red-50 border border-red-200 rounded-sm p-3" data-testid="review-errors">
          <div className="text-[12px] font-semibold text-red-800 mb-2 flex items-center gap-1.5">
            <AlertTriangle size={12} /> {errorKeys.length} validation error{errorKeys.length === 1 ? "" : "s"} to fix
          </div>
          <ul className="text-[11px] text-red-700 space-y-0.5 list-disc list-inside">
            {errorKeys.map((k) => <li key={k}>{errors[k]}</li>)}
          </ul>
        </div>
      ) : (
        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-[12px] text-emerald-800 flex items-center gap-2" data-testid="review-clean">
          <CheckCircle2 size={12} /> All sections look good. Ready to save.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-[12px]">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-[#71717A] mb-1">Project</div>
          <div className="font-semibold">{form.name || "—"}</div>
          <div className="text-[#52525B]">
            {(statusOptions.find((o) => o.key === form.status)?.label) || (form.status ? `Legacy: ${form.status}` : "—")}
            {form.expected_possession ? ` · ${form.expected_possession}` : ""}
          </div>
          <div className="text-[11px] text-[#71717A] mt-1">RERA: {form.rera_number || "—"} {form.rera_verified ? "(verified)" : ""}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-[#71717A] mb-1">Location</div>
          <div>{[form.main_area, form.sub_area].filter(Boolean).join(" · ") || "—"}</div>
          {form.private_address && <div className="text-[11px] text-[#71717A] mt-1">📍 {form.private_address}</div>}
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider font-semibold text-[#71717A] mb-1">Amenities ({String(form.amenities || "").split(",").filter(Boolean).length})</div>
        <div className="text-[12px] text-[#52525B]">{form.amenities || "—"}</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider font-semibold text-[#71717A] mb-1">Configurations ({liveConfigs.length})</div>
        {liveConfigs.length === 0 ? <div className="text-[12px] text-[#71717A]">None added.</div> : (
          <ul className="space-y-1 text-[12px]" data-testid="review-configs">
            {liveConfigs.map((c, i) => (
              <li key={i} className="flex items-center gap-2">
                <Layers size={11} className="text-[#0044CC]" />
                <span className="font-semibold">{bhkOptions.find((o) => o.key === c.config_type_key)?.label || c.config_type_key || "—"}</span>
                {c.variant_label && <span className="text-[#71717A]">· {c.variant_label}</span>}
                {(c.min_area || c.max_area) && <span className="text-[#71717A]">· {c.min_area}{c.max_area && c.min_area !== c.max_area ? `–${c.max_area}` : ""} {c.area_unit}</span>}
                {(c.min_price_input !== "" && c.min_price_input !== null) && (
                  <span className="text-[#71717A]">· {formatINRDisplay(toCanonicalINR(c.min_price_input, c.price_unit))}</span>
                )}
                {c.price_review_required && <Badge variant="outline" className="rounded-sm text-[9px] bg-amber-50 border-amber-300 text-amber-800">Review</Badge>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


// ─── Inventory editor (Phase I-B.2C) ───────────────────────────────────────
// Internal-only unit management. Uses isolated atomic endpoints; does NOT
// participate in the unified atomic Project/Config save (so a typo on a
// unit can never roll back project root edits).

const UNIT_STATUSES = [
  { key: "available", label: "Available", tone: "emerald" },
  { key: "hold",      label: "On Hold",   tone: "amber" },
  { key: "booked",    label: "Booked",    tone: "blue" },
  { key: "sold",      label: "Sold",      tone: "zinc" },
];

const STATUS_TONE = {
  available: "bg-emerald-50 text-emerald-800 border-emerald-200",
  hold:      "bg-amber-50 text-amber-800 border-amber-200",
  booked:    "bg-blue-50 text-blue-800 border-blue-200",
  sold:      "bg-zinc-100 text-zinc-700 border-zinc-200",
};

function InventoryEditor({ projectId }) {
  const [configs, setConfigs] = useState([]);
  const [selectedCid, setSelectedCid] = useState("");
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [historyUid, setHistoryUid] = useState(null);

  // Load canonical configs (non-archived)
  useEffect(() => {
    let cancelled = false;
    api.get(`/projects/${projectId}/configurations`).then((r) => {
      if (cancelled) return;
      const rows = (r.data?.configurations || []).filter((c) => !c.archived_at && c.source === "canonical");
      setConfigs(rows);
      if (rows.length > 0 && !selectedCid) setSelectedCid(rows[0].id);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [projectId, selectedCid]);

  const loadSummary = async () => {
    try {
      const r = await api.get(`/projects/${projectId}/inventory-summary`);
      setSummary(r.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadSummary(); }, [projectId]);

  const loadUnits = async () => {
    if (!selectedCid) return;
    setLoading(true);
    try {
      const r = await api.get(`/projects/${projectId}/configurations/${selectedCid}/units`);
      setUnits(r.data?.units || []);
    } catch (e) {
      toast.error(apiErrorMsg(e, "Could not load units"));
    }
    setLoading(false);
  };
  useEffect(() => { loadUnits(); }, [selectedCid]);

  if (configs.length === 0) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 text-[12px] text-amber-800" data-testid="inventory-no-configs">
        Save at least one canonical configuration before adding units. Go to the <span className="font-semibold">Configurations</span> tab to add 2 BHK / 3 BHK rows first.
      </div>
    );
  }

  const selectedConfig = configs.find((c) => c.id === selectedCid);

  return (
    <div className="space-y-4" data-testid="inventory-editor">
      <div className="text-[11px] text-[#52525B]">
        Exact unit data is INTERNAL ONLY. Public viewers see derived counts only — never unit codes, exact prices, or notes.
      </div>

      {/* Summary card */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[11px]" data-testid="inventory-summary">
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-2.5">
            <div className="text-[#71717A] uppercase tracking-wider text-[9px]">Configurations</div>
            <div className="text-[15px] font-semibold text-[#09090B]">{summary.configurations_count}</div>
          </div>
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-2.5">
            <div className="text-[#71717A] uppercase tracking-wider text-[9px]">Available Units</div>
            <div className="text-[15px] font-semibold text-emerald-700">{summary.available_count ?? "—"}</div>
          </div>
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-2.5">
            <div className="text-[#71717A] uppercase tracking-wider text-[9px]">Starting From</div>
            <div className="text-[15px] font-semibold text-[#0044CC] font-mono-data">{formatINRDisplay(summary.starting_price_inr)}</div>
          </div>
        </div>
      )}

      {/* Config selector + actions */}
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[180px]">
          <Label small>Configuration</Label>
          <Select value={selectedCid} onValueChange={setSelectedCid}>
            <SelectTrigger className="rounded-sm h-8 text-[12px]" data-testid="inventory-config-select">
              <SelectValue placeholder="Pick configuration" />
            </SelectTrigger>
            <SelectContent>
              {configs.map((c) => (
                <SelectItem key={c.id} value={c.id} data-testid={`inventory-config-opt-${c.config_type_key}`}>
                  {c.config_type_label || c.config_type_key}{c.variant_label ? ` · ${c.variant_label}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button type="button" size="sm" variant="outline" className="rounded-sm" onClick={() => setShowAdd(true)} data-testid="inventory-add-one-btn">
          <Plus size={11} className="mr-1" /> Add Unit
        </Button>
        <Button type="button" size="sm" variant="outline" className="rounded-sm" onClick={() => setShowBulk(true)} data-testid="inventory-bulk-btn">
          <Plus size={11} className="mr-1" /> Bulk Add
        </Button>
      </div>

      {/* Units grid */}
      {loading ? (
        <div className="text-[12px] text-[#71717A]">Loading units…</div>
      ) : units.length === 0 ? (
        <div className="border border-dashed border-[#D4D4D8] rounded-sm p-6 text-center text-[12px] text-[#71717A]" data-testid="inventory-empty">
          No units yet for this configuration. Click <span className="font-semibold">Add Unit</span> to start.
        </div>
      ) : (
        <div className="overflow-x-auto border border-[#E4E4E7] rounded-sm" data-testid="inventory-units-table">
          <table className="w-full text-[12px]">
            <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider text-[#52525B]">
              <tr>
                <th className="px-2 py-1.5 text-left">Tower</th>
                <th className="px-2 py-1.5 text-left">Unit</th>
                <th className="px-2 py-1.5 text-left">Floor</th>
                <th className="px-2 py-1.5 text-left">Size</th>
                <th className="px-2 py-1.5 text-left">Price (INR)</th>
                <th className="px-2 py-1.5 text-left">Status</th>
                <th className="px-2 py-1.5 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {units.map((u, i) => (
                <UnitRow
                  key={u.id}
                  u={u}
                  index={i}
                  projectId={projectId}
                  cid={selectedCid}
                  onChanged={() => { loadUnits(); loadSummary(); }}
                  onHistory={() => setHistoryUid(u.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && (
        <AddUnitDialog
          projectId={projectId} cid={selectedCid}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); loadUnits(); loadSummary(); }}
        />
      )}
      {showBulk && (
        <BulkAddUnitsDialog
          projectId={projectId} cid={selectedCid} configLabel={selectedConfig?.config_type_label || selectedConfig?.config_type_key}
          onClose={() => setShowBulk(false)}
          onSaved={() => { setShowBulk(false); loadUnits(); loadSummary(); }}
        />
      )}
      {historyUid && (
        <UnitHistoryDialog
          projectId={projectId} cid={selectedCid} uid={historyUid}
          onClose={() => setHistoryUid(null)}
        />
      )}
    </div>
  );
}

function UnitRow({ u, index, projectId, cid, onChanged, onHistory }) {
  const [busy, setBusy] = useState(false);
  const onStatus = async (newStatus) => {
    if (newStatus === u.status) return;
    setBusy(true);
    try {
      await api.patch(`/projects/${projectId}/configurations/${cid}/units/${u.id}`, { status: newStatus });
      toast.success(`Unit ${u.unit_code} → ${newStatus}`);
      onChanged();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Could not change status"));
    }
    setBusy(false);
  };
  const onArchive = async () => {
    if (!window.confirm(`Archive unit ${u.unit_code}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/projects/${projectId}/configurations/${cid}/units/${u.id}`);
      toast.success(`Unit ${u.unit_code} archived`);
      onChanged();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Archive failed"));
    }
    setBusy(false);
  };
  return (
    <tr className="border-t border-[#E4E4E7] hover:bg-[#FAFAFA]" data-testid={`unit-row-${index}`}>
      <td className="px-2 py-1.5">{u.tower_block || "—"}</td>
      <td className="px-2 py-1.5 font-mono-data text-[11px]" data-testid={`unit-${index}-code`}>{u.unit_code}</td>
      <td className="px-2 py-1.5">{u.floor ?? "—"}</td>
      <td className="px-2 py-1.5 font-mono-data text-[11px]">{u.size_value} {u.size_unit}</td>
      <td className="px-2 py-1.5 font-mono-data text-[11px]">{formatINRDisplay(u.canonical_price_inr)}</td>
      <td className="px-2 py-1.5">
        <Select value={u.status} onValueChange={onStatus} disabled={busy}>
          <SelectTrigger className={`rounded-sm h-6 text-[10px] border ${STATUS_TONE[u.status] || ""}`} data-testid={`unit-${index}-status-trigger`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {UNIT_STATUSES.map((s) => (
              <SelectItem key={s.key} value={s.key} data-testid={`unit-${index}-status-opt-${s.key}`}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="px-2 py-1.5">
        <div className="flex items-center gap-1">
          <Button type="button" size="sm" variant="ghost" onClick={onHistory} className="h-6 px-2 text-[10px]" data-testid={`unit-${index}-history-btn`}>
            History
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={onArchive} className="h-6 px-2 text-[10px] text-red-600" data-testid={`unit-${index}-archive-btn`}>
            <Trash2 size={9} />
          </Button>
        </div>
      </td>
    </tr>
  );
}

function AddUnitDialog({ projectId, cid, onClose, onSaved }) {
  const [u, setU] = useState({
    unit_code: "", tower_block: "", floor: "",
    size_value: "", size_unit: "sqft",
    canonical_price_inr: "", facing: "", status: "available", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!u.unit_code.trim()) { toast.error("Unit code is required"); return; }
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/configurations/${cid}/units`, {
        ...u,
        floor: u.floor === "" ? null : Number(u.floor),
        size_value: Number(u.size_value) || 0,
        canonical_price_inr: u.canonical_price_inr === "" ? null : Number(u.canonical_price_inr),
      });
      toast.success(`Unit ${u.unit_code} added`);
      onSaved();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Could not add unit"));
    }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="add-unit-dialog">
      <div className="bg-white border border-[#E4E4E7] rounded-sm shadow-lg w-full max-w-md p-5 space-y-3">
        <div className="text-[13px] font-semibold uppercase tracking-wider text-[#0044CC]">Add Unit</div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Unit Code" required value={u.unit_code} onChange={(v) => setU({ ...u, unit_code: v })} testid="add-unit-code" />
          <Field label="Tower / Block" value={u.tower_block} onChange={(v) => setU({ ...u, tower_block: v })} testid="add-unit-tower" />
          <Field label="Floor" type="number" value={u.floor} onChange={(v) => setU({ ...u, floor: v })} testid="add-unit-floor" />
          <Field label="Facing" value={u.facing} onChange={(v) => setU({ ...u, facing: v })} testid="add-unit-facing" />
          <Field label="Size" type="number" value={u.size_value} onChange={(v) => setU({ ...u, size_value: v })} testid="add-unit-size" />
          <div>
            <Label small>Size Unit</Label>
            <Select value={u.size_unit} onValueChange={(v) => setU({ ...u, size_unit: v })}>
              <SelectTrigger className="rounded-sm h-8 text-[12px]" data-testid="add-unit-size-unit"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sqft">sqft</SelectItem>
                <SelectItem value="sqyd">sqyd</SelectItem>
                <SelectItem value="sqm">sqm</SelectItem>
                <SelectItem value="acre">acre</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Field label="Price (INR)" type="number" value={u.canonical_price_inr} onChange={(v) => setU({ ...u, canonical_price_inr: v })} testid="add-unit-price" />
          <div>
            <Label small>Status</Label>
            <Select value={u.status} onValueChange={(v) => setU({ ...u, status: v })}>
              <SelectTrigger className="rounded-sm h-8 text-[12px]" data-testid="add-unit-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {UNIT_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label small>Internal Notes</Label>
          <Textarea rows={2} value={u.notes} onChange={(e) => setU({ ...u, notes: e.target.value })} className="rounded-sm" data-testid="add-unit-notes" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} className="text-[11px]" data-testid="add-unit-cancel">Cancel</Button>
          <Button type="button" onClick={save} disabled={busy} className="btn-brand rounded-sm" data-testid="add-unit-save">
            <Save size={11} className="mr-1.5" />{busy ? "Saving…" : "Add"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function BulkAddUnitsDialog({ projectId, cid, configLabel, onClose, onSaved }) {
  // Simple multi-row grid. User types one row per line: code,tower,floor,size,price
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const parseRows = () => {
    return String(text).split("\n").map((ln) => ln.trim()).filter(Boolean).map((ln) => {
      const parts = ln.split(",").map((p) => p.trim());
      const [code, tower, floor, size, price] = parts;
      return {
        unit_code: code || "",
        tower_block: tower || "",
        floor: floor ? Number(floor) : null,
        size_value: Number(size) || 0,
        size_unit: "sqft",
        canonical_price_inr: price ? Number(price) : null,
        facing: "",
        status: "available",
        notes: "",
      };
    });
  };
  const save = async () => {
    const units = parseRows();
    if (units.length === 0) { toast.error("Paste at least one row"); return; }
    if (units.some((u) => !u.unit_code)) { toast.error("Every row needs a unit code"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/projects/${projectId}/configurations/${cid}/units/bulk`, {
        idempotency_key: typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
        units,
      });
      toast.success(`${r.data?.created ?? units.length} units added`);
      onSaved();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Bulk add failed"));
    }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="bulk-add-dialog">
      <div className="bg-white border border-[#E4E4E7] rounded-sm shadow-lg w-full max-w-xl p-5 space-y-3">
        <div className="text-[13px] font-semibold uppercase tracking-wider text-[#0044CC]">Bulk Add Units {configLabel ? `· ${configLabel}` : ""}</div>
        <div className="text-[11px] text-[#52525B]">
          Paste one unit per line, comma-separated: <span className="font-mono-data">code, tower, floor, size, price</span>. Example:
          <pre className="bg-[#FAFAFA] p-2 rounded-sm mt-1 text-[10px] border border-[#E4E4E7]">A-101, Tower A, 1, 1050, 5500000
A-102, Tower A, 1, 1050, 5600000
A-201, Tower A, 2, 1050, 5700000</pre>
        </div>
        <Textarea rows={10} value={text} onChange={(e) => setText(e.target.value)} className="rounded-sm font-mono-data text-[12px]" placeholder="Paste rows here…" data-testid="bulk-add-textarea" />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} className="text-[11px]" data-testid="bulk-add-cancel">Cancel</Button>
          <Button type="button" onClick={save} disabled={busy} className="btn-brand rounded-sm" data-testid="bulk-add-save">
            <Save size={11} className="mr-1.5" />{busy ? "Saving…" : `Add ${parseRows().length || 0} units`}
          </Button>
        </div>
      </div>
    </div>
  );
}

function UnitHistoryDialog({ projectId, cid, uid, onClose }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/projects/${projectId}/configurations/${cid}/units/${uid}/history`)
      .then((r) => setEvents(r.data?.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [projectId, cid, uid]);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="unit-history-dialog">
      <div className="bg-white border border-[#E4E4E7] rounded-sm shadow-lg w-full max-w-md p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-[13px] font-semibold uppercase tracking-wider text-[#0044CC]">Unit History</div>
          <Button type="button" variant="ghost" size="sm" onClick={onClose} data-testid="unit-history-close">
            <X size={12} />
          </Button>
        </div>
        {loading ? (
          <div className="text-[12px] text-[#71717A]">Loading…</div>
        ) : events.length === 0 ? (
          <div className="text-[12px] text-[#71717A]">No events recorded.</div>
        ) : (
          <ul className="space-y-1.5 text-[11px]">
            {events.map((e) => (
              <li key={e.id} className="flex items-start gap-2 border-b border-[#F4F4F5] pb-1.5">
                <Badge variant="outline" className="rounded-sm text-[9px]">{e.event_type}</Badge>
                <div className="flex-1">
                  <div className="text-[#52525B]">{e.at?.slice(0, 19).replace("T", " ")}</div>
                  <div className="text-[#09090B] font-mono-data text-[10px]">
                    {e.event_type === "status_change" && `${e.prev_value} → ${e.new_value}`}
                    {e.event_type === "price_change" && `₹${e.prev_value} → ₹${e.new_value}`}
                    {e.event_type === "created" && "Unit created"}
                    {e.event_type === "archived" && "Unit archived"}
                    {e.event_type === "verified" && `Verified at ${e.new_value}`}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
